import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiRouterLine } from '@remixicon/react';
import {
  ExternalLink,
  InfraField,
  InfraSection,
  InfraStack,
  InfraTag,
  InfraTagValue,
  InfraTagRow,
  Text,
} from '../ui/InfraPrimitives';

export const TransitGatewayInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const transitGatewayId = annotations['transit-gateway.infrastructure/tgw-id'];
  const tgwAttachmentId = annotations['transit-gateway.infrastructure/tgw-attachment-id'];
  const tgwRouteTableId = annotations['transit-gateway.infrastructure/tgw-route-table-id'];
  const redisTgwId = annotations['transit-gateway.infrastructure/redis-tgw-id'];
  const redisTgwStatus = annotations['transit-gateway.infrastructure/redis-tgw-status'];
  const subscriptionId = annotations['transit-gateway.infrastructure/subscription-id'];
  const vpcId = annotations['transit-gateway.infrastructure/vpc-id'];
  const vpcCidr = annotations['transit-gateway.infrastructure/vpc-cidr'];
  const awsRegion = annotations['transit-gateway.infrastructure/aws-region'];
  const consoleUrl = annotations['transit-gateway.infrastructure/console-url'];

  const hasTransitGatewayInfo = transitGatewayId || tgwAttachmentId;

  if (!hasTransitGatewayInfo) {
    return null;
  }

  return (
    <InfoCard title="🌐 AWS Transit Gateway Information" icon={<RiRouterLine size={20} />}>
      <InfraStack>
        <InfraSection title="🔀 Transit Gateway">
          {transitGatewayId && (
            <InfraField label="Transit Gateway ID:" value={transitGatewayId} />
          )}
          {tgwAttachmentId && (
            <InfraField label="VPC Attachment ID:" value={tgwAttachmentId} />
          )}
          {tgwRouteTableId && (
            <InfraField label="Route Table ID:" value={tgwRouteTableId} />
          )}
          {awsRegion && (
            <InfraField
              label="AWS Region:"
              value={<InfraTagValue>{awsRegion}</InfraTagValue>}
              mono={false}
            />
          )}
          {consoleUrl && (
            <InfraField
              label="AWS Console:"
              value={
                <ExternalLink href={consoleUrl}>View in AWS Console</ExternalLink>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="☁️ Redis Cloud Connection">
          {subscriptionId && (
            <InfraField label="Subscription ID:" value={subscriptionId} />
          )}
          {redisTgwId && redisTgwId !== '0' && (
            <InfraField label="Redis TGW ID:" value={redisTgwId} />
          )}
          {redisTgwStatus && (
            <InfraField
              label="Status:"
              value={
                <InfraTagRow>
                  <InfraTag>{redisTgwStatus}</InfraTag>
                </InfraTagRow>
              }
              mono={false}
            />
          )}
        </InfraSection>

        <InfraSection title="🌐 VPC Configuration">
          {vpcId && <InfraField label="VPC ID:" value={vpcId} />}
          {vpcCidr && (
            <InfraField
              label="VPC CIDR:"
              value={<InfraTagValue>{vpcCidr}</InfraTagValue>}
              mono={false}
            />
          )}
        </InfraSection>

        <div
          style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 6,
            padding: '1rem',
          }}
        >
          <Text variant="body-medium">
            <strong>⚠️ Manual Steps Required:</strong>
          </Text>
          <Text variant="body-medium" as="div">
            <ol style={{ marginTop: 8, paddingLeft: 20 }}>
              <li>Go to Redis Cloud Console → Subscriptions → Transit Gateway</li>
              <li>Create Attachment for this Transit Gateway</li>
              <li>Accept the attachment request in AWS VPC Console</li>
              <li>Add Consumer CIDRs (VPC CIDR: {vpcCidr || 'N/A'})</li>
              <li>Update your application to use Redis private endpoints</li>
            </ol>
          </Text>
        </div>

        <Text variant="body-medium">
          📚{' '}
          <ExternalLink href="https://docs.redis.com/latest/rc/security/vpc-peering/aws-transit-gateway/">
            Transit Gateway Documentation
          </ExternalLink>
        </Text>
      </InfraStack>
    </InfoCard>
  );
};
